import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import pageRepository from '@/features/pages/repositories/PageRepository';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import type { Page } from '@/features/pages/types/page.types';
import { invalidatePageQueries, invalidateTagQueries } from '@/shared/query/queryKeys';

/**
 * Imports notes from the legacy Google Drive backup flow into local storage.
 *
 * Google Drive gives us already-diffed add/update/delete lists, separate from
 * the main sync engine. This hook applies those lists through PageRepository,
 * then invalidates query-backed reads; if the open page changed, it bumps the
 * page version so the uncontrolled editor remounts with the fresh content.
 */
export function usePageSyncImport() {
  const queryClient = useQueryClient();

  return useCallback(
    async (notesToAdd: Page[], notesToUpdate: Page[], toDeleteFromLocal: Page[]) => {
      await pageRepository.bulkUpsertPages([...notesToAdd, ...notesToUpdate]);

      if (toDeleteFromLocal.length) {
        const archived = toDeleteFromLocal.map(p => ({
          ...p,
          archivedAt: p.archivedAt || Date.now(),
          updatedAt: Date.now(),
        }));
        await pageRepository.bulkUpsertPages(archived);
      }

      void invalidateTagQueries(queryClient);
      await invalidatePageQueries(queryClient);

      const { currentPageId, updatePageVersion } = usePageStore.getState();
      const currentPageChanged =
        !!currentPageId &&
        [...notesToUpdate, ...toDeleteFromLocal].some(p => p.id === currentPageId);
      if (currentPageChanged) {
        updatePageVersion();
      }
    },
    [queryClient]
  );
}
